import React from 'react';
import {
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Folder, OpenInNew } from '@mui/icons-material';
import {
  Artifact,
  platformApi,
  Project,
  ReviewCommentStatusCounts,
} from '../../services/platformApi';
import { formatTranslatedLabel } from '../../i18n';
import { useLanguage } from '../../LanguageContext';
import { getStatusColor } from '../projectConstants';
import { workflowStages } from './workspaceConfig';

interface WorkflowTabProps {
  project: Project | null;
  reviewCommentStatusCounts: ReviewCommentStatusCounts;
  stageKey: string;
  storyDocuments: Artifact[];
}

export const WorkflowTab: React.FC<WorkflowTabProps> = ({
  project,
  reviewCommentStatusCounts,
  stageKey,
  storyDocuments,
}) => {
  const { locale, t } = useLanguage();
  const stage = workflowStages.find((item) => item.key === stageKey);
  const stageDocuments =
    stageKey === 'design'
      ? storyDocuments.filter((artifact) => artifact.metadata_json?.design_ready === true || artifact.status === 'design_ready')
      : stageKey === 'publishing'
        ? storyDocuments.filter(
            (artifact) => artifact.metadata_json?.publishing_ready === true || artifact.status === 'publishing_ready'
          )
        : [];

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
          {stage?.icon || <Folder />}
          <Typography variant="h6">{formatTranslatedLabel(t, stage?.key || 'workflow')}</Typography>
          <Chip size="small" label={formatTranslatedLabel(t, stage?.status)} variant="outlined" />
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, minHeight: 120 }}>
              <Typography variant="body2" color="text.secondary">
                {t('currentStatus')}
              </Typography>
              <Typography variant="h6">{formatTranslatedLabel(t, stage?.status)}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, minHeight: 120 }}>
              <Typography variant="body2" color="text.secondary">
                {t('storyDocuments')}
              </Typography>
              <Typography variant="h6">{stageDocuments.length}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, minHeight: 120 }}>
              <Typography variant="body2" color="text.secondary">
                {t('openReviews')}
              </Typography>
              <Typography variant="h6">{reviewCommentStatusCounts.open}</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {(stageKey === 'design' || stageKey === 'publishing') && (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('storyDocument')}</TableCell>
                <TableCell>{t('status')}</TableCell>
                <TableCell>{t('format')}</TableCell>
                <TableCell>{t('updated')}</TableCell>
                <TableCell align="right">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stageDocuments.map((artifact) => (
                <TableRow key={artifact.id} hover>
                  <TableCell>
                    <Typography fontWeight={700}>{artifact.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {artifact.description || artifact.file_name || t('storyDocument')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={formatTranslatedLabel(t, artifact.status)} color={getStatusColor(artifact.status)} />
                  </TableCell>
                  <TableCell>{String(artifact.metadata_json?.document_format || 'document').toUpperCase()}</TableCell>
                  <TableCell>{artifact.created_at ? new Date(artifact.created_at).toLocaleString(locale) : t('notRecorded')}</TableCell>
                  <TableCell align="right">
                    {project && (
                      <Button
                        href={platformApi.getProjectArtifactDownloadUrl(project.id, artifact.id)}
                        target="_blank"
                        rel="noreferrer"
                        startIcon={<OpenInNew />}
                      >
                        {t('open')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {stageDocuments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography color="text.secondary" align="center" sx={{ py: 5 }}>
                      {t('noStoryDocumentsReadyYet')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
};
